import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import styles from "./SearchView.module.css";
import NewOffersSwiper from "../components/NewOffersSwiper";
import { ReactComponent as BedIcon } from "../images/bed.svg";
import { ReactComponent as BuildingIcon } from "../images/house.svg";
import { ReactComponent as PurchaseIcon } from "../images/tag.svg";
import { getDisplayValue } from "../utils/criteriaHelpers";

const formatWithApostrophe = (value) => {
  if (!value && value !== 0) return '';
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/'/g, '')) : value;
  if (isNaN(numValue)) return value;
  const digitsOnly = numValue.toString().replace(/[^\d]/g, '');
  return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
};

const SearchView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { currentUser, isAuthenticated, loadingAuth } = useAuth();
  const [demands, setDemands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [criteriaGroups, setCriteriaGroups] = useState([]);

  useEffect(() => {
    const fetchDemands = async () => {
      try {
        const response = await authFetch(
          `${BASE_URL}/property/latest/demands?limit=30`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch demands");
        }

        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const data = await response.json();
          setDemands(data);
        } else {
          const text = await response.text();
          throw new Error(
            `Server returned a non-JSON response. Check your API route. Response was: "${text.substring(
              0,
              50
            )}..."`
          );
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching demands:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDemands();

    // const intervalId = setInterval(fetchDemands, 60000);

    return () => {
      // Polling disabled to prevent property changes during animation
      // clearInterval(intervalId);
    };
  }, []);

  // Fetch criteria groups for translation
  useEffect(() => {
    const fetchCriteriaGroups = async () => {
      try {
        const langCode = i18n.language.split("-")[0];
        const url = `${BASE_URL}/property/criteria-options/${langCode}`;
        const response = await authFetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch criteria groups: ${response.status}`);
        }
        const data = await response.json();
        setCriteriaGroups(data.data || []);
      } catch (err) {
        console.error("Error fetching criteria groups:", err);
      }
    };
    fetchCriteriaGroups();
  }, [i18n.language]);

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      if (!id) {
        setError("No property ID provided.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setProperty(null);
      setError(null);

      try {
        const response = await authFetch(`${BASE_URL}/property/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setProperty(data.data);
        console.log("Fetched property data for SearchView:", data.data);
      } catch (err) {
        console.error(`Failed to fetch property ${id}:`, err);
        setError("Failed to load property details. Please try again.");
        toast.error("Failed to load property details.");
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyDetails();
  }, [id]); // Re-fetch if ID changes (removed 't' dependency)

  // Authorization: must be logged in and must own this property
  useEffect(() => {
    if (loadingAuth || loading) return;

    if (!isAuthenticated) {
      navigate("/auth/login", { replace: true });
      return;
    }

    if (property && currentUser?.id && property.userId !== currentUser.id) {
      toast.error(
        t("access_denied") ||
          "Access denied. You don't have permission to view this property."
      );
      navigate("/", { replace: true });
    }
  }, [loadingAuth, loading, isAuthenticated, property, currentUser, navigate, t]);

  if (loadingAuth || loading) {
    return (
      <div className={styles.message}>{t("Loading_property_details")}</div>
    );
  }

  if (error) {
    return <div className={styles.message}>{error}</div>;
  }

  if (!property) {
    return (
      <div className={styles.message}>{t("Property_not_found_message")}</div>
    );
  }

  // Block render if unauthorized (navigating away, but prevent flash of content)
  if (!isAuthenticated || (property && currentUser?.id && property.userId !== currentUser.id)) {
    return null;
  }

  // Destructure data from the API response
  const {
    price,
    currency,
    bedrooms,
    squareMeters,
    units,
    propertyType,
    offeringType,
    PropertyAddresses,
    DemandPropertyCriterium, // This contains mustCriteria and shouldCriteria
  } = property;

  const mainAddress = PropertyAddresses?.[0]; // Get the first address object

  console.log(DemandPropertyCriterium);

  const mustCriteria = DemandPropertyCriterium?.mustCriteria || {};
  const shouldCriteria = DemandPropertyCriterium?.shouldCriteria || {};

  // Helper function to get display value from criteriaGroups
  const getDisplayValueFromIdentifier = (groupIdentifier, optionIdentifier) => {
    if (!criteriaGroups || criteriaGroups.length === 0) {
      // Fallback: format the identifier nicely if criteriaGroups not loaded
      return optionIdentifier.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }

    // Find the group
    const group = criteriaGroups.find(g => g.identifier === groupIdentifier);
    if (!group) {
      // Try translation key as fallback
      const translationKey = `${groupIdentifier}.${optionIdentifier}`;
      return t(translationKey, {
        defaultValue: optionIdentifier.split('_').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ')
      });
    }

    // Find the option within the group
    const option = group.values?.find(v => v.identifier === optionIdentifier);
    if (option) {
      return option.value || option.display; // Return translated value
    }

    // Final fallback: formatted identifier
    return optionIdentifier.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Helper to render criteria badges
  const renderCriteriaBadges = (criteriaObject) => {
    const badges = [];

    for (const key in criteriaObject) {
      if (Object.hasOwnProperty.call(criteriaObject, key)) {
        const value = criteriaObject[key];
        if (value) {
          // Ensure value is an array, then flatMap to handle nested arrays if they exist
          const valuesToDisplay = Array.isArray(value) ? value : [value];
          valuesToDisplay.forEach((item) => {
            if (item) {
              // Get the translated display value from criteriaGroups
              const displayValue = getDisplayValueFromIdentifier(key, item);

              badges.push(
                <span key={`${key}-${item}`} className={styles.criteriaBadge}>
                  {displayValue}
                </span>
              );
            }
          });
        }
      }
    }
    return badges.length > 0 ? (
      badges
    ) : (
      <span className={styles.criteriaBadge}>{t("No specific criteria")}</span>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.leftPanel}>
        {!isLoading && !error && demands.length > 0 && (
          <div className={styles.newSearchesHeader}>
            <h2 className={styles.newSearchesTitle}>{t("new_demands")}</h2>
          </div>
        )}
        {demands.length > 0 && (
          <div className={styles.propertyList}>
            <NewOffersSwiper items={demands} className={styles.mySwiper} />
          </div>
        )}
      </div>

      <div className={styles.rightPanel}>
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>{t("Search_Details")}</h1>
          <div style={{ width: "80px" }}></div>
        </div>

        <div className={styles.card}>
          <div className={styles.priceSection}>
            {currency} {price ? formatWithApostrophe(price) : ''}
          </div>
          <div className={styles.locationText}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={styles.locationIcon}
            >
              <path
                d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
                fill="currentColor"
              />{" "}
              {/* Use currentColor for fill */}
            </svg>
            {mainAddress?.city || "N/A"}, {mainAddress?.zipCode || "N/A"}
          </div>

          <div className={styles.infoBadges}>
            <span className={styles.badge}>
              <BedIcon className={styles.icon} />
              {bedrooms || units || "N/A"} {t("Beds")}
            </span>
            <span className={styles.badge}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={styles.icon}
              >
                <path
                  d="M19 5H5C3.9 5 3 5.9 3 7V17C3 18.1 3.9 19 5 19H19C20.1 19 21 18.1 21 17V7C21 5.9 20.1 5 19 5ZM11 17H5V7H11V17ZM19 17H13V7H19V17Z"
                  fill="currentColor"
                />
              </svg>
              {squareMeters || "N/A"} {t("sqft")}
            </span>
            <span className={styles.badge}>
              <BuildingIcon className={styles.icon} />
              {propertyType ? t(propertyType.toLowerCase()) : "N/A"}
            </span>
            <span className={styles.badge}>
              <PurchaseIcon className={styles.icon} />
              {offeringType ? t(offeringType.toLowerCase()) : "N/A"}
            </span>
          </div>
        </div>

        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>{t("Location")}</h2>
          <div className={styles.locationList}>
            <div className={styles.locationItem}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={styles.locationIcon}
              >
                <path
                  d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
                  fill="currentColor"
                />
              </svg>
              {mainAddress?.city || "N/A"}, {mainAddress?.zipCode || "N/A"},{" "}
              {mainAddress?.countryName || "N/A"}
            </div>
            <div className={styles.locationItem}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={styles.locationIcon}
              >
                <path
                  d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
                  fill="currentColor"
                />
              </svg>
              {mainAddress?.city || "N/A"}, {mainAddress?.zipCode || "N/A"}
            </div>
            <div className={styles.locationItem}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={styles.flagIcon}
              >
                <path
                  d="M14.4 6L14 4H5V21H7V14H12.6L13 16H20V6H14.4Z"
                  fill="currentColor"
                />
              </svg>
              {mainAddress?.countryName || "N/A"}
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>{t("Search_Criteria")}</h2>
          <div className={styles.criteriaSection}>
            <div className={styles.criteriaGroup}>
              <h3 className={styles.criteriaHeading}>{t("Must_Have")}</h3>
              <div className={styles.criteriaBadges}>
                {renderCriteriaBadges(mustCriteria)}
              </div>
            </div>
            <div className={styles.criteriaGroup}>
              <h3 className={styles.criteriaHeading}>{t("Should_Have")}</h3>
              <div className={styles.criteriaBadges}>
                {renderCriteriaBadges(shouldCriteria)}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <button onClick={() => navigate(-1)} className={styles.backButton}>
            {t("Back")}
          </button>
          {id ? (
            <Link
              to={`/SearchProperty/${id}`}
              className={styles.textLink} // Apply a class for the Link to remove default underline if needed
            >
              <button className={styles.navButton}>{t("edit")}</button>
            </Link>
          ) : (
            <button
              className={`${styles.navButton2} ${styles.navButtonDisabled}`}
              disabled
            >
              {t("edit")} (ID missing)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchView;
